import axios from 'axios'

// CSRF Token management
let csrfToken = null

/**
 * Fetch CSRF token from backend
 * This token is required for all POST/PUT/DELETE/PATCH requests
 * @returns {Promise<string>} The CSRF token
 */
export async function fetchCsrfToken() {
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/csrf-token`, {
      withCredentials: true,
    })
    csrfToken = response.data.csrfToken
    console.log('CSRF token fetched successfully')
    return csrfToken
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error.message)
    throw error
  }
}

/**
 * Get current CSRF token
 * @returns {string|null} The current CSRF token or null if not fetched yet
 */
export function getCsrfToken() {
  return csrfToken
}

const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // assicura che i cookie vengano inviati
})

// Interceptor per aggiungere CSRF token a tutte le richieste non-safe
api.interceptors.request.use(
  (config) => {
    // Aggiungi CSRF token per metodi non-safe (POST, PUT, DELETE, PATCH)
    const unsafeMethods = ['post', 'put', 'delete', 'patch']
    if (config.method && unsafeMethods.includes(config.method.toLowerCase())) {
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      } else {
        console.warn('CSRF token not available for request:', config.url)
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor globale per gestire 401 e 403 (CSRF)
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    console.error('API Error:', error.response?.status, error.message)

    // Gestione errori CSRF (403 Forbidden con messaggio specifico)
    if (error.response?.status === 403 && error.response?.data?.error === 'Invalid CSRF token') {
      console.warn('CSRF token invalid, attempting to refresh...')

      // Tenta di ottenere un nuovo token CSRF
      try {
        await fetchCsrfToken()

        // Riprova la richiesta originale con il nuovo token
        if (!originalRequest._retry) {
          originalRequest._retry = true
          originalRequest.headers['X-CSRF-Token'] = csrfToken
          return api(originalRequest)
        }
      } catch (refreshError) {
        console.error('Failed to refresh CSRF token:', refreshError)
      }
    }

    // Gestione errori di autenticazione
    if (error.response?.status === 401) {
      console.error('Sessione scaduta o non valida')

      // Evita redirect se siamo già in login o in pagine admin
      const currentPath = window.location.pathname
      const isLoginPage = currentPath === '/login'
      const isAdminPage = currentPath.startsWith('/admin')

      // Se non siamo già nella pagina di login, reindirizza
      if (!isLoginPage && !isAdminPage) {
        localStorage.removeItem('isAuthenticated')
        // Usa router invece di window.location
        if (window.router) {
          window.router.push('/login')
        } else {
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

export default api
